import React, { useState } from 'react';
import './EligibilityForm.css';

export interface EligibilityFormData {
  // Step 1: Goal Selection
  goal: 'buy-home' | 'refinance' | 'reverse-mortgage' | '';
  
  // Step 2: Property Type
  propertyType: 'single-family' | 'condo-townhouse' | 'multi-family' | 'investment' | '';
  
  // Step 3: Budget Range
  budgetRange: 'under-250k' | '250k-500k' | '500k-1m' | 'over-1m' | '';
  
  // Step 4: Property Ownership
  ownsProperty: 'yes' | 'no' | '';
  
  // Step 5: Pre-approval
  preApproved: 'yes' | 'no' | '';
  
  // Step 6: Name
  firstName: string;
  lastName: string;
  
  // Step 7: Contact
  phone: string;
  email: string;
  
  // Step 8: Thank you (no fields)
}

interface EligibilityFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EligibilityFormData) => void;
}

const EligibilityForm: React.FC<EligibilityFormProps> = ({ isOpen, onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<EligibilityFormData>({
    goal: 'buy-home',
    propertyType: 'condo-townhouse',
    budgetRange: '250k-500k', // Default as shown in design
    ownsProperty: 'yes', // Default as shown in design
    preApproved: 'yes', // Default as shown in design
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  });

  const totalSteps = 8;

  const handleInputChange = (field: keyof EligibilityFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      // If moving to step 8, submit the form
      if (nextStep === 8) {
        onSubmit(formData);
      }
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
    // Don't reset - stay on thank you page
  };

  const handleClose = () => {
    onClose();
    // Reset form when closing
    setCurrentStep(1);
    setFormData({
      goal: 'buy-home',
      propertyType: 'condo-townhouse',
      budgetRange: '250k-500k',
      ownsProperty: 'yes',
      preApproved: 'yes',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
    });
  };

  const getProgressPercentage = () => {
    // Progress percentages as per design
    const progressMap: { [key: number]: number } = {
      1: 5,
      2: 14,
      3: 29,
      4: 46,
      5: 61,
      6: 76,
      7: 95,
      8: 100,
    };
    return progressMap[currentStep] || Math.round((currentStep / totalSteps) * 100);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="eligibility-form-step eligibility-form-step-1">
            <h2 className="eligibility-form-step-title">
              Let's start to get better rate. What is your goal?
            </h2>
            <div className="eligibility-form-goal-cards">
              <div 
                className={`eligibility-form-goal-card ${formData.goal === 'buy-home' ? 'selected' : ''}`}
                onClick={() => handleInputChange('goal', 'buy-home')}
              >
                <div className="eligibility-form-goal-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M8 16L24 6L40 16V38C40 39.1046 39.1046 40 38 40H10C8.89543 40 8 39.1046 8 38V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 16L24 24L40 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="32" cy="20" r="2" fill="currentColor"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-goal-title">Buy new home</h3>
                <p className="eligibility-form-goal-description">
                  Buying a home is a big step! We'll help you secure the best rates and guide you through a smooth, stress-free process.
                </p>
              </div>
              
              <div 
                className={`eligibility-form-goal-card ${formData.goal === 'refinance' ? 'selected' : ''}`}
                onClick={() => handleInputChange('goal', 'refinance')}
              >
                <div className="eligibility-form-goal-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M8 16L24 6L40 16V38C40 39.1046 39.1046 40 38 40H10C8.89543 40 8 39.1046 8 38V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M24 16V24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20 20H28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-goal-title">Refinance a home</h3>
                <p className="eligibility-form-goal-description">
                  Refinancing can lower your payments or unlock equity. Let us find the best rates to fit your financial goals.
                </p>
              </div>
              
              <div 
                className={`eligibility-form-goal-card ${formData.goal === 'reverse-mortgage' ? 'selected' : ''}`}
                onClick={() => handleInputChange('goal', 'reverse-mortgage')}
              >
                <div className="eligibility-form-goal-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M8 16L24 6L40 16V38C40 39.1046 39.1046 40 38 40H10C8.89543 40 8 39.1046 8 38V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="24" cy="20" r="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M20 20L24 16L28 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-goal-title">Reverse Mortgage</h3>
                <p className="eligibility-form-goal-description">
                  A reverse mortgage can provide extra income in retirement. We'll help you navigate the process and find the right solution for you.
                </p>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="eligibility-form-step eligibility-form-step-2">
            <h2 className="eligibility-form-step-title">
              What type of property are you interested in?
            </h2>
            <div className="eligibility-form-property-cards">
              <div 
                className={`eligibility-form-property-card ${formData.propertyType === 'single-family' ? 'selected' : ''}`}
                onClick={() => handleInputChange('propertyType', 'single-family')}
              >
                <div className="eligibility-form-property-icon">
                  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                    <path d="M8 16L24 6L40 16V38C40 39.1046 39.1046 40 38 40H10C8.89543 40 8 39.1046 8 38V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 16L24 24L40 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-property-title">Single-family home</h3>
              </div>
              
              <div 
                className={`eligibility-form-property-card ${formData.propertyType === 'condo-townhouse' ? 'selected' : ''}`}
                onClick={() => handleInputChange('propertyType', 'condo-townhouse')}
              >
                <div className="eligibility-form-property-icon">
                  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                    <path d="M8 16L24 6L40 16V38C40 39.1046 39.1046 40 38 40H10C8.89543 40 8 39.1046 8 38V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 16L24 24L40 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="20" y="12" width="8" height="8" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-property-title">Condo/Townhouse</h3>
              </div>
              
              <div 
                className={`eligibility-form-property-card ${formData.propertyType === 'multi-family' ? 'selected' : ''}`}
                onClick={() => handleInputChange('propertyType', 'multi-family')}
              >
                <div className="eligibility-form-property-icon">
                  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                    <path d="M8 16L24 6L40 16V38C40 39.1046 39.1046 40 38 40H10C8.89543 40 8 39.1046 8 38V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 16L24 24L40 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="12" y="12" width="8" height="8" stroke="currentColor" strokeWidth="2"/>
                    <rect x="28" y="12" width="8" height="8" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-property-title">Multi-family unit</h3>
              </div>
              
              <div 
                className={`eligibility-form-property-card ${formData.propertyType === 'investment' ? 'selected' : ''}`}
                onClick={() => handleInputChange('propertyType', 'investment')}
              >
                <div className="eligibility-form-property-icon">
                  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                    <path d="M8 16L24 6L40 16V38C40 39.1046 39.1046 40 38 40H10C8.89543 40 8 39.1046 8 38V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 16L24 24L40 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="24" cy="20" r="2" fill="currentColor"/>
                    <path d="M20 20L24 16L28 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-property-title">Investment property</h3>
              </div>
            </div>
            
            <div className="eligibility-form-info-box">
              <div className="eligibility-form-info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                </svg>
              </div>
              <p className="eligibility-form-info-text">
                Refinancing can lower your payments or unlock equity. Let us find the best rates to fit your financial goals.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="eligibility-form-step eligibility-form-step-3">
            <h2 className="eligibility-form-step-title">
              What is your target budget range?
            </h2>
            <div className="eligibility-form-budget-cards">
              <div 
                className={`eligibility-form-budget-card ${formData.budgetRange === 'under-250k' ? 'selected' : ''}`}
                onClick={() => handleInputChange('budgetRange', 'under-250k')}
              >
                <div className="eligibility-form-budget-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-budget-title">Under $250,000</h3>
              </div>
              
              <div 
                className={`eligibility-form-budget-card ${formData.budgetRange === '250k-500k' ? 'selected' : ''}`}
                onClick={() => handleInputChange('budgetRange', '250k-500k')}
              >
                <div className="eligibility-form-budget-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="8" cy="12" r="3" fill="currentColor"/>
                    <circle cx="16" cy="12" r="3" fill="currentColor"/>
                    <rect x="5" y="10" width="14" height="4" fill="currentColor"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-budget-title">$250,000 - $500,000</h3>
              </div>
              
              <div 
                className={`eligibility-form-budget-card ${formData.budgetRange === '500k-1m' ? 'selected' : ''}`}
                onClick={() => handleInputChange('budgetRange', '500k-1m')}
              >
                <div className="eligibility-form-budget-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="8" cy="12" r="3" fill="currentColor"/>
                    <circle cx="16" cy="12" r="3" fill="currentColor"/>
                    <rect x="5" y="10" width="14" height="4" fill="currentColor"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-budget-title">$500,000 - $1M</h3>
              </div>
              
              <div 
                className={`eligibility-form-budget-card ${formData.budgetRange === 'over-1m' ? 'selected' : ''}`}
                onClick={() => handleInputChange('budgetRange', 'over-1m')}
              >
                <div className="eligibility-form-budget-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="8" cy="12" r="3" fill="currentColor"/>
                    <circle cx="16" cy="12" r="3" fill="currentColor"/>
                    <rect x="5" y="10" width="14" height="4" fill="currentColor"/>
                  </svg>
                </div>
                <h3 className="eligibility-form-budget-title">Over $1M</h3>
              </div>
            </div>
            
            <div className="eligibility-form-info-box">
              <div className="eligibility-form-info-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                </svg>
              </div>
              <p className="eligibility-form-info-text">
                Refinancing can lower your payments or unlock equity. Let us find the best rates to fit your financial goals.
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="eligibility-form-step eligibility-form-step-4">
            <h2 className="eligibility-form-step-title">
              Do you currently own a property?
            </h2>
            <div className="eligibility-form-yes-no-cards">
              <div 
                className={`eligibility-form-yes-no-card ${formData.ownsProperty === 'yes' ? 'selected' : ''}`}
                onClick={() => handleInputChange('ownsProperty', 'yes')}
              >
                <div className="eligibility-form-yes-no-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="eligibility-form-yes-no-text">Yes</span>
              </div>
              
              <div 
                className={`eligibility-form-yes-no-card ${formData.ownsProperty === 'no' ? 'selected' : ''}`}
                onClick={() => handleInputChange('ownsProperty', 'no')}
              >
                <div className="eligibility-form-yes-no-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9 9L15 15M15 9L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="eligibility-form-yes-no-text">No</span>
              </div>
            </div>
            
            <div className="eligibility-form-info-box">
              <div className="eligibility-form-info-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                </svg>
              </div>
              <p className="eligibility-form-info-text">
                Refinancing can lower your payments or unlock equity. Let us find the best rates to fit your financial goals.
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="eligibility-form-step eligibility-form-step-5">
            <h2 className="eligibility-form-step-title">
              Are you pre-approved for a mortgage?
            </h2>
            <div className="eligibility-form-yes-no-cards">
              <div 
                className={`eligibility-form-yes-no-card ${formData.preApproved === 'yes' ? 'selected' : ''}`}
                onClick={() => handleInputChange('preApproved', 'yes')}
              >
                <div className="eligibility-form-yes-no-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="eligibility-form-yes-no-text">Yes</span>
              </div>
              
              <div 
                className={`eligibility-form-yes-no-card ${formData.preApproved === 'no' ? 'selected' : ''}`}
                onClick={() => handleInputChange('preApproved', 'no')}
              >
                <div className="eligibility-form-yes-no-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9 9L15 15M15 9L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="eligibility-form-yes-no-text">No</span>
              </div>
            </div>
            
            <div className="eligibility-form-info-box">
              <div className="eligibility-form-info-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                </svg>
              </div>
              <p className="eligibility-form-info-text">
                Refinancing can lower your payments or unlock equity. Let us find the best rates to fit your financial goals.
              </p>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="eligibility-form-step eligibility-form-step-6">
            <h2 className="eligibility-form-step-title">
              What is your name?
            </h2>
            <div className="eligibility-form-input-fields">
              <div className="eligibility-form-input-field">
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="First name"
                  required
                />
              </div>
              <div className="eligibility-form-input-field">
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>
            
            <div className="eligibility-form-trust-box">
              <p className="eligibility-form-trust-text">
                Trusted by over <strong>1,000 happy homeowners</strong>, with an average satisfaction rating of <strong>4.9/5</strong>.
              </p>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="eligibility-form-step eligibility-form-step-7">
            <h2 className="eligibility-form-step-title">
              Please leave your contacts.
            </h2>
            <div className="eligibility-form-input-fields">
              <div className="eligibility-form-input-field">
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Phone number"
                  required
                />
              </div>
              <div className="eligibility-form-input-field">
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Email address"
                  required
                />
              </div>
            </div>
            
            <div className="eligibility-form-disclaimer">
              <p className="eligibility-form-disclaimer-text">
                By clicking the "Next" button above, I agree to <a href="#" className="eligibility-form-link">Privacy Policy</a> and <a href="#" className="eligibility-form-link">Terms of Use</a>. By clicking "Next", I provide express written consent for calls or text messages to the phone number provided (or any other number Better may find) for marketing purposes, using an automated telephone dialing system, even if the number is on a Do-Not-Call list. I understand that my consent is not required to make a purchase.
              </p>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="eligibility-form-step eligibility-form-step-8">
            <h2 className="eligibility-form-step-title">
              Thank you for your interest. We will contact you soon.
            </h2>
            
            <div className="eligibility-form-broker-card">
              <h3 className="eligibility-form-broker-title">Your mortgage broker.</h3>
              <div className="eligibility-form-broker-content">
                <div className="eligibility-form-broker-avatar">
                  <div className="eligibility-form-broker-avatar-circle"></div>
                </div>
                <div className="eligibility-form-broker-info">
                  <div className="eligibility-form-broker-name">Valentino Adams</div>
                  <div className="eligibility-form-broker-details">
                    <div className="eligibility-form-broker-detail-item">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M10.0003 4.99996V9.99996L13.3337 11.6666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Mon - Fri, 9am to 7pm</span>
                    </div>
                    <div className="eligibility-form-broker-detail-item">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M16.5625 3.75H3.4375C2.57417 3.75 1.875 4.45 1.875 5.3125V14.6875C1.875 15.55 2.575 16.25 3.4375 16.25H16.5625C17.425 16.25 18.125 15.55 18.125 14.6875V5.3125C18.125 4.44917 17.425 3.75 16.5625 3.75Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4.375 6.25L10 10.625L15.625 6.25" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>valentino@mortgage.com</span>
                    </div>
                    <div className="eligibility-form-broker-detail-item">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M17.4999 14.5376C16.8894 13.9222 15.4108 13.0246 14.6928 12.6627C13.7582 12.1917 13.6811 12.154 12.9467 12.6996C12.4569 13.064 12.1311 13.3889 11.5576 13.2666C10.9832 13.1444 9.73762 12.4543 8.64631 11.3671C7.555 10.2791 6.82555 8.99658 6.70329 8.42549C6.58021 7.85358 6.91089 7.53111 7.27192 7.04043C7.78065 6.34872 7.74209 6.23303 7.3072 5.29844C6.96832 4.57145 6.04358 3.10598 5.42654 2.49796C4.76601 1.84564 4.76601 1.96133 4.34016 2.13775C3.99345 2.28373 3.66084 2.46116 3.34649 2.66781C2.73109 3.07644 2.38975 3.41614 2.15098 3.92651C1.91138 4.43688 1.80471 5.63322 3.03797 7.87409C4.27123 10.115 5.13689 11.2604 6.9273 13.0467C8.71934 14.833 10.0962 15.7939 12.1098 16.9229C14.6009 18.3178 15.556 18.0462 16.068 17.8075C16.58 17.5695 16.9214 17.2306 17.3316 16.6152C17.5386 16.3013 17.716 15.969 17.8617 15.6224C18.0389 15.199 18.1546 15.199 17.5007 14.5385L17.4999 14.5376Z" stroke="#0578EC" strokeWidth="1.4"/>
                      </svg>
                      <span>+1 (714) 655-1681</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="eligibility-form-info-box">
              <p className="eligibility-form-info-text">
                We've received your information and will be in touch shortly to help you achieve your goals. Our team is committed to providing personalized support and finding the best solution for your needs. Expect a call or email soon!
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="eligibility-form-overlay" onClick={currentStep === 8 ? () => {} : handleClose}>
      <div className="eligibility-form-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header with Logo and Call Button */}
        <div className="eligibility-form-header">
          <div className="eligibility-form-logo">
            <svg width="24" height="28" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 10.7047L4 8V20H0V10.7047Z" fill="url(#paint0_linear_eligibility)"/>
              <path d="M7 6.58967L11 4V20H7V6.58967Z" fill="url(#paint1_linear_eligibility)"/>
              <path d="M14 2.51414L18 0V20H14V2.51414Z" fill="url(#paint2_linear_eligibility)"/>
              <defs>
                <linearGradient id="paint0_linear_eligibility" x1="2" y1="8" x2="2" y2="20" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#77BBFF"/>
                  <stop offset="1" stopColor="#0265C7"/>
                </linearGradient>
                <linearGradient id="paint1_linear_eligibility" x1="9" y1="4" x2="9" y2="20" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#77BBFF"/>
                  <stop offset="1" stopColor="#0265C7"/>
                </linearGradient>
                <linearGradient id="paint2_linear_eligibility" x1="16" y1="0" x2="16" y2="20" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#77BBFF"/>
                  <stop offset="1" stopColor="#0265C7"/>
                </linearGradient>
              </defs>
            </svg>
            <span className="eligibility-form-logo-text">Mortgage<span className="plus-sign">+</span></span>
          </div>
          <a href="tel:+17146551681" className="eligibility-form-call-button">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="#0578EC" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Call us</span>
            <span className="eligibility-form-call-number">+1 (714) 655-1681</span>
          </a>
        </div>

        {/* Progress Bar */}
        <div className="eligibility-form-progress">
          <div className="eligibility-form-progress-bar">
            <div 
              className="eligibility-form-progress-fill"
              style={{ width: `${getProgressPercentage()}%` }}
            />
            <div 
              className="eligibility-form-progress-avatar"
              style={{ left: `${getProgressPercentage()}%` }}
            >
              <div className="eligibility-form-progress-avatar-circle"></div>
            </div>
          </div>
          <div className="eligibility-form-progress-text">
            {getProgressPercentage()}%
          </div>
        </div>

        <div className="eligibility-form-content">
          {renderStepContent()}
        </div>

        <div className="eligibility-form-actions">
          {currentStep > 1 && currentStep < 8 && (
            <button 
              className="eligibility-form-button eligibility-form-button-secondary" 
              onClick={handlePrevious}
            >
              Previous step
            </button>
          )}
          <div className="eligibility-form-actions-spacer" />
          {currentStep < totalSteps ? (
            <button 
              className="eligibility-form-button eligibility-form-button-primary" 
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !formData.goal) || 
                (currentStep === 2 && !formData.propertyType) ||
                (currentStep === 3 && !formData.budgetRange) ||
                (currentStep === 4 && !formData.ownsProperty) ||
                (currentStep === 5 && !formData.preApproved) ||
                (currentStep === 6 && (!formData.firstName || !formData.lastName)) ||
                (currentStep === 7 && (!formData.phone || !formData.email))
              }
            >
              Next step →
            </button>
          ) : (
            <button className="eligibility-form-button eligibility-form-button-primary" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EligibilityForm;

